import {
  Download as DownloadIcon,
  InfoOutlined,
  Refresh as RefreshIcon,
  Search as SearchIcon
} from '@mui/icons-material'
import {
  Backdrop,
  Chip,
  CircularProgress,
  FormControl,
  InputAdornment,
  LinearProgress,
  MenuItem,
  Select,
  SelectChangeEvent,
  TextField,
  Tooltip,
} from '@mui/material'
import dayjs from 'dayjs'
import utc from 'dayjs/plugin/utc'
import { Workbook } from 'exceljs'
import { isEmpty, size } from 'lodash'
import { useContext, useEffect, useMemo, useRef, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { useDebounce } from 'use-debounce'
import { listMicroLearnings } from '../../api/api'
import { MicroLearningListItem, ScoreTooltip } from '../../api/models'
import EmptyBox from '../../assets/empty_box.svg'
import FetchError from '../../assets/fetch_error.svg'
import { CustomTable } from '../../components/CustomTable'
import { ScoreDetailsPopover } from '../../components/ScoreDetailsPopover'
import { AppStateContext } from '../../state/AppProvider'
import styles from './MicroLearnings.module.css'

dayjs.extend(utc)

const PAGE_SIZE = 15

const MicroLearnings = () => {
  const appStateContext = useContext(AppStateContext)
  const navigate = useNavigate()
  const persistedListState = appStateContext?.state.microLearningListState
  const dispatch = appStateContext?.dispatch

  const [searchQuery, setSearchQuery] = useState(persistedListState?.searchQuery || '')
  const [debouncedSearch] = useDebounce(searchQuery, 300)
  const [statusFilter, setStatusFilter] = useState<'All' | 'Pending Review' | 'Approved' | 'Dismissed' | 'Not Processed'>(
    (persistedListState?.statusFilter as
      | 'All'
      | 'Pending Review'
      | 'Approved'
      | 'Dismissed'
      | 'Not Processed') || 'All'
  )
  const [sortOrder, setSortOrder] = useState(persistedListState?.sortOrder || 'Highest Score')
  const [page, setPage] = useState(persistedListState?.page || 1)
  const [microLearnings, setMicroLearnings] = useState<MicroLearningListItem[]>([])
  const [totalItems, setTotalItems] = useState<number>(0)
  const [hasFetchError, setHasFetchError] = useState<boolean>(false)
  const [isBackdropOpen, setIsBackdropOpen] = useState<boolean>(false)
  const [order, setOrder] = useState<'asc' | 'desc'>(persistedListState?.columnSortOrder || 'desc')
  const [orderBy, setOrderBy] = useState<string>(persistedListState?.columnSortBy || '')
  const hasInitializedSearchEffect = useRef(false)
  const hasInitializedFilterSortEffect = useRef(false)
  const latestFetchRequestId = useRef(0)
  const [scorePopover, setScorePopover] = useState<{
    anchorEl: HTMLElement | null
    tooltip: ScoreTooltip
  }>({
    anchorEl: null,
    tooltip: { experiment_codes: [], without_sales_evidence: [], missing_quantitative_results: [], kpi_mismatch: false },
  })

  const mapColumnNameToApiField = (columnName: string): string => {
    switch (columnName) {
      case 'ID':
        return 'ml_id'
      case 'Title':
        return 'title'
      case 'Pipeline Experiments':
        return 'pipeline_experiments_count'
      case 'Score':
        return 'score'
      case 'Status':
        return 'status'
      case 'Created On':
        return 'created_at'
      default:
        return 'created_at'
    }
  }

  const mapSortOrderToApiParams = (sortLabel: string) => {
    switch (sortLabel) {
      case 'Newest First':
        return { sort_by: 'created_at', sort_order: 'desc' }
      case 'Oldest First':
        return { sort_by: 'created_at', sort_order: 'asc' }
      case 'Highest Score':
        return { sort_by: 'score', sort_order: 'desc' }
      case 'Lowest Score':
        return { sort_by: 'score', sort_order: 'asc' }
      default:
        return { sort_by: 'created_at', sort_order: 'desc' }
    }
  }

  const mapStatusFilterToApiValue = (status: string) => {
    switch (status) {
      case 'Pending Review':
        return 'pending_review'
      case 'Approved':
        return 'approved'
      case 'Dismissed':
        return 'dismissed'
      case 'Not Processed':
        return 'not_processed'
      default:
        return 'all'
    }
  }

  const fetchMicroLearnings = async (currentPage: number = page) => {
    const requestId = ++latestFetchRequestId.current
    setIsBackdropOpen(true)

    // Use column-based sorting if orderBy is set, otherwise use dropdown sorting
    let sort_by: string
    let sort_order: string
    if (orderBy && orderBy.trim() !== '') {
      sort_by = mapColumnNameToApiField(orderBy)
      sort_order = order
    } else {
      const params = mapSortOrderToApiParams(sortOrder)
      sort_by = params.sort_by
      sort_order = params.sort_order
    }

    const status = mapStatusFilterToApiValue(statusFilter)

    const response = await listMicroLearnings({
      search: debouncedSearch || undefined,
      status: status !== 'all' ? status : undefined,
      sort_by,
      sort_order,
      page: currentPage,
      per_page: PAGE_SIZE,
    })

    if (requestId !== latestFetchRequestId.current) {
      return
    }

    if (!response) {
      setHasFetchError(true)
      setMicroLearnings([])
      setIsBackdropOpen(false)
      return
    }

    const totalPagesFromResponse = response.pagination?.total_pages || 1
    if (totalPagesFromResponse > 0 && currentPage > totalPagesFromResponse) {
      setPage(totalPagesFromResponse)
      setIsBackdropOpen(false)
      return
    }

    setMicroLearnings(response.data || [])
    setTotalItems(response.pagination?.total_items || 0)
    setHasFetchError(false)
    setIsBackdropOpen(false)
  }

  // Initial list load should honor persisted pagination state.
  useEffect(() => {
    fetchMicroLearnings(page)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page])

  const resetToFirstPageOrFetch = () => {
    if (page === 1) {
      fetchMicroLearnings(1)
      return
    }
    setPage(1)
  }

  useEffect(() => {
    if (!hasInitializedSearchEffect.current) {
      hasInitializedSearchEffect.current = true
      return
    }

    if (size(debouncedSearch) >= 3 || isEmpty(debouncedSearch)) {
      resetToFirstPageOrFetch()
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [debouncedSearch])

  useEffect(() => {
    if (!hasInitializedFilterSortEffect.current) {
      hasInitializedFilterSortEffect.current = true
      return
    }

    resetToFirstPageOrFetch()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [statusFilter, sortOrder, order, orderBy])

  const totalPages = Math.ceil(totalItems / PAGE_SIZE)

  const getCount = useMemo(() => {
    return {
      start: page * PAGE_SIZE - PAGE_SIZE + 1,
      end: Math.min(page * PAGE_SIZE, totalItems),
      total: totalItems,
    }
  }, [page, totalItems])


  const getStatusColor = (status: string) => {
    switch (status) {
      case 'approved':
        return 'rgba(46, 125, 50, 1)'
      case 'pending_review':
        return 'rgba(237, 108, 2, 1)'
      case 'dismissed':
        return 'rgba(211, 47, 47, 1)'
      case 'not_processed':
        return 'rgba(107, 107, 107, 1)'
      default:
        return 'rgba(107, 107, 107, 1)'
    }
  }

  const getStatusDisplayLabel = (status: string) => {
    switch (status) {
      case 'approved':
        return 'Approved'
      case 'pending_review':
        return 'Pending Review'
      case 'dismissed':
        return 'Dismissed'
      case 'not_processed':
        return 'Not Processed'
      case 'regenerating':
        return 'Regenerating'
      default:
        return status
    }
  }

  const getScoreColor = (tier: 'high' | 'medium' | 'low') => {
    if (tier === 'high') return 'rgba(76, 175, 80, 1)' // Green
    if (tier === 'medium') return 'rgba(255, 152, 0, 1)' // Orange
    return 'rgba(244, 67, 54, 1)' // Red
  }

  const formatDate = (dateString: string) => {
    return dayjs.utc(dateString).format('MM/DD/YYYY')
  }

  const handleRefresh = () => {
    fetchMicroLearnings(1)
  }

  useEffect(() => {
    if (!dispatch) {
      return
    }

    dispatch({
      type: 'SET_MICRO_LEARNING_LIST_STATE',
      payload: {
        statusFilter,
        sortOrder,
        searchQuery: debouncedSearch,
        page,
        columnSortBy: orderBy,
        columnSortOrder: order,
      },
    })
  }, [statusFilter, sortOrder, debouncedSearch, page, orderBy, order])

  const navigateToDetail = (mlId: string) => {
    navigate(`/app/micro-learnings/${encodeURIComponent(mlId)}`)
  }

  const fetchAllMicroLearnings = async (): Promise<MicroLearningListItem[]> => {
    // Determine sorting parameters (same logic as fetchMicroLearnings)
    let sort_by: string
    let sort_order: string
    if (orderBy && orderBy.trim() !== '') {
      sort_by = mapColumnNameToApiField(orderBy)
      sort_order = order
    } else {
      const params = mapSortOrderToApiParams(sortOrder)
      sort_by = params.sort_by
      sort_order = params.sort_order
    }

    const status = mapStatusFilterToApiValue(statusFilter)
    const allMicroLearnings: MicroLearningListItem[] = []
    const MAX_PER_PAGE = 100 // Backend limit

    // Fetch first page to get total count
    const firstResponse = await listMicroLearnings({
      search: debouncedSearch || undefined,
      status: status !== 'all' ? status : undefined,
      sort_by,
      sort_order,
      page: 1,
      per_page: MAX_PER_PAGE,
    })

    if (!firstResponse) {
      return []
    }

    allMicroLearnings.push(...(firstResponse.data || []))
    const totalPages = firstResponse.pagination?.total_pages || 1

    // Fetch remaining pages if any
    if (totalPages > 1) {
      const remainingPages = Array.from({ length: totalPages - 1 }, (_, i) => i + 2)
      const pagePromises = remainingPages.map(pageNum =>
        listMicroLearnings({
          search: debouncedSearch || undefined,
          status: status !== 'all' ? status : undefined,
          sort_by,
          sort_order,
          page: pageNum,
          per_page: MAX_PER_PAGE,
        })
      )

      const responses = await Promise.all(pagePromises)
      responses.forEach(response => {
        if (response && response.data) {
          allMicroLearnings.push(...response.data)
        }
      })
    }

    return allMicroLearnings
  }

  const handleExport = async () => {
    try {
      setIsBackdropOpen(true)

      // Fetch all microlearnings respecting filters and sorting
      const allMicroLearnings = await fetchAllMicroLearnings()

      setIsBackdropOpen(false)

      if (allMicroLearnings.length === 0) {
        console.warn('No microlearnings to export')
        return
      }

      const workbook = new Workbook()
      const ws = workbook.addWorksheet('Micro Learnings')

      // Header row
      const headerRow = ws.addRow([
        'ID',
        'Title',
        'Pipeline Experiments',
        'GLA Strategic Priority',
        'Score',
        'Status',
        'Operation Unit',
        'Created On',
        'Experiments',
      ])
      headerRow.font = { bold: true }
      headerRow.eachCell(cell => {
        cell.fill = {
          type: 'pattern',
          pattern: 'solid',
          fgColor: { argb: 'FFF5F5F5' },
        }
      })

      // Data rows
      for (const ml of allMicroLearnings) {
        const dataRow = ws.addRow([
          ml.ml_id,
          ml.title,
          ml.pipeline_experiments_count > 0 ? ml.pipeline_experiments_count : 'N/D',
          ml.gla_strategic_priority.join(', '),
          ml.status === 'not_processed' ? 'N/A' : ml.score.total,
          getStatusDisplayLabel(ml.status),
          ml.ous.join(', '),
          formatDate(ml.created_at),
          ml.experiment_count,
        ])

        // Apply color-coded font colors
        const scoreCell = dataRow.getCell(5) // Score column (moved due to new column)
        if (ml.status !== 'not_processed') {
          if (ml.score.total > 80) {
            scoreCell.font = { color: { argb: 'FF4CAF50' }, bold: true } // Green
          } else if (ml.score.total >= 50) {
            scoreCell.font = { color: { argb: 'FFFF9800' }, bold: true } // Orange
          } else {
            scoreCell.font = { color: { argb: 'FFF44336' }, bold: true } // Red
          }
        }

        const statusCell = dataRow.getCell(6) // Status column (moved due to new column)
        const getStatusColorHex = (status: string) => {
          switch (status) {
            case 'approved':
              return '2E7D32'
            case 'pending_review':
              return 'ED6C02'
            case 'dismissed':
              return 'D32F2F'
            case 'not_processed':
              return '6B6B6B'
            default:
              return '6B6B6B'
          }
        }
        statusCell.font = { color: { argb: 'FF' + getStatusColorHex(ml.status) }, bold: true }
      }

      // Auto-width columns
      ws.columns.forEach(col => {
        col.width = 20
      })

      // Generate buffer and download
      const buffer = await workbook.xlsx.writeBuffer()
      const blob = new Blob([buffer], {
        type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
      })
      const url = URL.createObjectURL(blob)
      const a = document.createElement('a')
      const now = new Date()
      const timestamp = now.toISOString().replace(/:/g, '_')
      a.href = url
      a.download = `Micro_Learnings_${timestamp}.xlsx`
      a.click()
      URL.revokeObjectURL(url)
    } catch (error) {
      console.error('Error exporting data:', error)
      setIsBackdropOpen(false)
    }
  }

  const handleScoreInfoClick = (event: React.MouseEvent<HTMLDivElement>, tooltip: ScoreTooltip) => {
    setScorePopover({ anchorEl: event.currentTarget, tooltip })
  }

  const handleScorePopoverClose = () => {
    setScorePopover(prev => ({ ...prev, anchorEl: null }))
  }

  const handleRequestSort = (property: string) => {
    const isAsc = orderBy === property && order === 'asc'
    setOrder(isAsc ? 'desc' : 'asc')
    setOrderBy(property)
    // Reset dropdown sort when using column sorting
    setSortOrder('Newest First')
  }

  const tableData = microLearnings.map(ml => ({
    hasBackground: false,
    cell: [
      <div key={`id-${ml.ml_id}`} className={styles.tableCellContainer}>
        <button
          type='button'
          className={styles.detailsLink}
          onClick={() => navigateToDetail(ml.ml_id)}>
          {ml.ml_id}
        </button>
      </div>,
      <div key={`title-${ml.ml_id}`} className={styles.titleCell}>
        <Tooltip title={ml.title}>
          <p className={styles.tableCellTitle}>{ml.title}</p>
        </Tooltip>
        <div className={styles.titleTags}>
          <Chip
            label={`${ml.experiment_count} experiments`}
            size="small"
            className={styles.experimentChip}
          />
          {ml.potential_other_learnings &&
            (ml.experiments_scale_potential ?? []).some(e => e.status === 'Completed') && (
              <Chip
                label="Potential other learning"
                size="small"
                className={styles.potentialOtherLearningChip}
              />
            )}
        </div>
      </div>,
      <div key={`pipeline-experiments-${ml.ml_id}`} className={styles.pipelineExperimentsCell}>
        <Chip
          label={ml.pipeline_experiments_count > 0 ? ml.pipeline_experiments_count : 'N/D'}
          size="small"
          className={styles.pipelineExperimentsChip}
          classes={{ label: styles.pipelineExperimentsChipLabel }}
        />
      </div>,
      <div key={`gla-${ml.ml_id}`}>
        {(() => {
          const sortedPriorities = [...ml.gla_strategic_priority].sort((a: string, b: string) => {
            if (a === 'Other') return 1;
            if (b === 'Other') return -1;
            return 0;
          });
          return (
            <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
              {sortedPriorities.slice(0, 1).map((gla: string, idx: number) => (
                <Tooltip key={`gla-${idx}`} title={gla}>
                  <Chip
                    label={gla}
                    size="small"
                    className={styles.categoryChip}
                    classes={{ label: styles.categoryChipLabel }}
                  />
                </Tooltip>
              ))}
              {sortedPriorities.length > 1 && (
                <Tooltip
                  title={
                    <div>
                      {sortedPriorities.slice(1).map((gla: string, idx: number) => (
                        <div key={idx}>{gla}</div>
                      ))}
                    </div>
                  }>
                  <Chip
                    label={`+${sortedPriorities.length - 1}`}
                    size="small"
                    className={styles.categoryChip}
                    classes={{ label: styles.categoryChipLabel }}
                  />
                </Tooltip>
              )}
            </div>
          );
        })()}
      </div>,
      <div key={`score-${ml.ml_id}`} className={styles.scoreCell}>
        {ml.status === 'not_processed' ? (
          <div className={styles.pipelineExperimentsCell}>
            <Chip
              label="N/A"
              title="Not Applicable"
              size="small"
              className={styles.pipelineExperimentsChip}
              classes={{ label: styles.pipelineExperimentsChipLabel }}
            />
          </div>
        ) : (
          <div className={styles.scoreContainer}>
            <span className={styles.scoreValue} style={{ color: getScoreColor(ml.score.tier) }}>
              {ml.score.total}
            </span>
            <div className={styles.scoreProgressBarWrapper}>
              <LinearProgress
                variant="determinate"
                value={ml.score.total}
                className={styles.scoreProgressBar}
                classes={{ bar: styles.scoreProgressBarFill }}
                sx={{
                  '& .MuiLinearProgress-bar': {
                    backgroundColor: getScoreColor(ml.score.tier),
                  },
                }}
              />
            </div>
            <div
              onClick={e => handleScoreInfoClick(e, ml.score.tooltip)}
              className={styles.infoIconContainer}>
              <InfoOutlined className={styles.infoIcon} />
            </div>
          </div>
        )}
      </div>,
      <div key={`status-${ml.ml_id}`} className={styles.statusCell}>
        <Chip
          label={getStatusDisplayLabel(ml.status)}
          size="small"
          className={styles.statusChip}
          style={{
            backgroundColor: 'transparent',
            borderColor: getStatusColor(ml.status),
            color: getStatusColor(ml.status),
          }}
        />
      </div>,
      <div key={`ous-${ml.ml_id}`} className={styles.ousCell}>
        {ml.ous.slice(0, 2).map((ou: string, idx: number) => (
          <Chip
            key={`ou-${idx}`}
            label={ou}
            size="small"
            className={styles.ouChip}
            classes={{ label: styles.ouChipLabel }}
          />
        ))}
        {ml.ous.length > 2 && (
          <Tooltip
            title={
              <div>
                {ml.ous.slice(2).map((ou: string, idx: number) => (
                  <div key={idx}>{ou}</div>
                ))}
              </div>
            }>
            <Chip
              label={`+${ml.ous.length - 2}`}
              size="small"
              className={styles.ouChip}
              classes={{ label: styles.ouChipLabel }}
            />
          </Tooltip>
        )}
      </div>,
      <div key={`date-${ml.ml_id}`} className={styles.tableCellContainer}>
        <p className={styles.tableCellText}>{formatDate(ml.created_at)}</p>
      </div>,
      <div key={`actions-${ml.ml_id}`}>
        <button
          className={styles.detailsButton}
          onClick={() => navigateToDetail(ml.ml_id)}>
          Details
        </button>
      </div>,
    ],
  }))

  return (
    <div className={styles.container}>
      <div className={styles.header}>
        <h1 className={styles.headerTitle}>Micro Learnings</h1>
        <p className={styles.headerDescription}>
          Review the AI-generated micro-learning and decide how to proceed.
        </p>
      </div>

      <div className={styles.filtersBar}>
        <TextField
          size='small'
          placeholder="Search micro-learnings..."
          value={searchQuery}
          onChange={e => setSearchQuery(e.target.value)}
          className={styles.searchInput}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon className={styles.searchIcon} />
              </InputAdornment>
            ),
          }}
        />

        <FormControl className={styles.filterSelect}>
          <Select
            size='small'
            value={statusFilter}
            onChange={(e: SelectChangeEvent) => setStatusFilter(e.target.value as typeof statusFilter)}
            displayEmpty>
            <MenuItem value="All">All</MenuItem>
            <MenuItem value="Pending Review">Pending Review</MenuItem>
            <MenuItem value="Approved">Approved</MenuItem>
            <MenuItem value="Dismissed">Dismissed</MenuItem>
            <MenuItem value="Not Processed">Not Processed</MenuItem>
          </Select>
        </FormControl>

        <FormControl className={styles.filterSelect}>
          <Select
            size='small'
            value={sortOrder}
            onChange={(e: SelectChangeEvent) => {
              setSortOrder(e.target.value)
              // Reset column-based sorting when using dropdown
              setOrderBy('')
            }}
            displayEmpty>
            <MenuItem value="Newest First">Newest First</MenuItem>
            <MenuItem value="Oldest First">Oldest First</MenuItem>
            <MenuItem value="Highest Score">Highest Score</MenuItem>
            <MenuItem value="Lowest Score">Lowest Score</MenuItem>
          </Select>
        </FormControl>

        <button onClick={handleRefresh} className={styles.actionButton}>
          <RefreshIcon className={styles.actionIcon} />
          <span className={styles.actionText}>Refresh</span>
        </button>

        <button onClick={handleExport} className={styles.actionButton}>
          <DownloadIcon className={styles.actionIcon} />
          <span className={styles.actionText}>Export</span>
        </button>
      </div>

      {hasFetchError || isEmpty(microLearnings) ? (
        <div className={styles.noExperimentsContainer}>
          {hasFetchError ? <img src={FetchError} /> : <img src={EmptyBox} />}
          <p className={styles.noExperimentsHeader}>
            {hasFetchError ? 'Error Fetching the Requests' : 'No requests found'}
          </p>
          <p className={styles.noExperimentsText}>
            {hasFetchError
              ? 'There was an issue retrieving the requests. Check your connection and try again. If the error persists, contact the support team.'
              : 'Try adjusting your search or filter criteria'}
          </p>
        </div>
      ) : (
        <>
          <div className={styles.tableContainer}>
            <CustomTable
              headTitles={['ID', 'Title', 'Pipeline Experiments', 'GLA Strategic Priority', 'Score', 'Status', 'Operation Unit', 'Created On', 'Actions']}
              order={order}
              orderBy={orderBy}
              data={tableData}
              onRequestSort={handleRequestSort}
              onPageChange={setPage}
              page={page}
              total={totalPages}
              isPaginated={true}
              sortableColumns={['ID', 'Title', 'Pipeline Experiments', 'Score', 'Status', 'Created On']}
              paginationInfo={
                <div className={styles.paginationInfo}>
                  <p>
                    Showing {getCount.start} to {getCount.end} of {getCount.total} items
                  </p>
                </div>
              }
              optionalText={
                <div className={styles.tableTextContainer}>
                  <p className={styles.tableText}>
                    This experience uses Generative AI. Always verify critical information.
                  </p>
                </div>
              }
            />
          </div>
        </>
      )}

      <Backdrop
        sx={{ color: '#fff', zIndex: theme => theme.zIndex.drawer + 1 }}
        open={isBackdropOpen}>
        <CircularProgress color="inherit" />
      </Backdrop>

      <ScoreDetailsPopover
        anchorEl={scorePopover.anchorEl}
        tooltip={scorePopover.tooltip}
        onClose={handleScorePopoverClose}
      />
    </div>
  )
}

export default MicroLearnings
